import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';

@Injectable({ providedIn: 'root' })
export class SessionService {
  private static readonly EXPIRED_KEY = 'yama_session_expired';

  /** AuthService sottoscrive questo Subject per resettare il proprio stato. */
  readonly sessionExpired$ = new Subject<void>();

  private timer: ReturnType<typeof setTimeout> | null = null;
  private handling = false;

  constructor(private readonly router: Router) {}

  /** Avvia (o riavvia) il watchdog di scadenza token. Da chiamare dopo ogni login e all'avvio. */
  async watchExpiry(): Promise<void> {
    this.clearTimer();
    try {
      const session = await fetchAuthSession();
      const exp = session.tokens?.idToken?.payload?.['exp'] as number | undefined;
      if (!exp) return;
      this.scheduleAt(exp);
    } catch {
      // nessuna sessione attiva, nulla da fare
    }
  }

  private scheduleAt(exp: number): void {
    const msUntilExpiry = exp * 1000 - Date.now();
    if (msUntilExpiry <= 0) {
      this.handleExpired();
      return;
    }
    // clamp a MAX_INT per evitare overflow su setTimeout
    this.timer = setTimeout(async () => {
      try {
        // Amplify potrebbe aver già rinnovato il token; ricontrolliamo prima di scadere
        const session = await fetchAuthSession();
        const newExp = session.tokens?.idToken?.payload?.['exp'] as number | undefined;
        if (newExp && newExp * 1000 > Date.now()) {
          this.scheduleAt(newExp);
          return;
        }
      } catch { /* token davvero scaduto */ }
      this.handleExpired();
    }, Math.min(msUntilExpiry, 2_147_483_647));
  }

  clearTimer(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  async handleExpired(): Promise<void> {
    if (this.handling) return;
    if (this.router.url.startsWith('/auth/login')) return;
    this.handling = true;
    this.clearTimer();
    sessionStorage.setItem(SessionService.EXPIRED_KEY, '1');
    this.sessionExpired$.next();
    try { await signOut(); } catch { /* ignoriamo errori di signOut */ }
    this.handling = false;
    void this.router.navigate(['/auth/login']);
  }

  /** Restituisce true se la sessione era scaduta (e cancella il flag). */
  wasExpired(): boolean {
    const flag = sessionStorage.getItem(SessionService.EXPIRED_KEY);
    if (flag) {
      sessionStorage.removeItem(SessionService.EXPIRED_KEY);
      return true;
    }
    return false;
  }
}
